import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  RiArrowLeftSLine,
  RiSparkling2Line,
  RiCheckLine,
  RiGasStationLine,
  RiQuestionMark,
  RiArrowRightLine,
  RiTimerFlashLine,
  RiGroupLine,
  RiFireLine,
  RiHistoryLine,
  RiInboxLine,
} from "react-icons/ri";
import { useUser } from "@utils/store/user/useUser";
import { useCusAuth } from "@utils/store/auth/useCusAuth";
import { buildXContextHeader } from "@utils/api/headers";
import {
  saveGasSurvey,
  estimateCycleFromSurvey,
  LAST_ORDER_AGO_CHIPS,
  LAST_ORDER_AGO_DAYS_MAX,
  mapDaysToLastOrderAgoKey,
  type GasSurveyRecord,
  type LastOrderAgoKey,
} from "./surveyStorage";
import { postGasSurvey } from "./gasSurveyApi";
import toast from "react-hot-toast";
import { toToastMessage } from "@utils/toastMessage";

/* ── 色票（對齊首頁）── */
const C = {
  page: "#f1f4f9",
  ink: "#1f2937",
  muted: "#8b909c",
  blue: "#2196f3",
  green: "#1d9e75",
  greenTint: "#e1f5ee",
  heroGradient: "linear-gradient(165deg, #10B981 0%, #06B6D4 60%, #3B82F6 100%)",
};

const Grain = ({ o = 0.03 }: { o?: number }) => (
  <div
    style={{
      position: "absolute",
      inset: 0,
      backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.05'/%3E%3C/svg%3E")`,
      backgroundRepeat: "repeat",
      backgroundSize: 200,
      opacity: o,
      pointerEvents: "none",
      borderRadius: "inherit",
      mixBlendMode: "soft-light",
    }}
  />
);

/* ── 選項 chips ── */

const CYCLE_CHIPS: { label: string; days: number }[] = [
  { label: "1 週", days: 7 },
  { label: "2 週", days: 14 },
  { label: "約 1 個月", days: 30 },
  { label: "1~2個月", days: 45 },
  { label: "2~3個月", days: 75 },
  { label: "3~4個月", days: 105 },
  { label: "4~5個月", days: 135 },
  { label: "約半年", days: 180 },
  { label: "半年以上", days: 210 },
  { label: "一年以上", days: 365 },
];

const KG_CHIPS: { label: string; kg: number | null }[] = [
  { label: "4 kg", kg: 4 },
  { label: "16 kg", kg: 16 },
  { label: "20 kg", kg: 20 },
  { label: "50 kg", kg: 50 },
  { label: "不確定", kg: null },
];

const HOUSEHOLD_CHIPS: { label: string; value: string }[] = [
  { label: "1 人", value: "1" },
  { label: "2 人", value: "2" },
  { label: "3–4 人", value: "3-4" },
  { label: "5 人以上", value: "5+" },
];

const USAGE_CHIPS: { label: string; value: string; hint: string }[] = [
  { label: "瓦斯爐煮飯", value: "cooking", hint: "日常煮食" },
  { label: "瓦斯熱水器", value: "hot_water", hint: "淋浴洗澡" },
  { label: "泡茶", value: "tea", hint: "煮水泡茶" },
];

const SPARE_CHIPS: { label: string; value: boolean | null }[] = [
  { label: "有備瓶", value: true },
  { label: "沒有", value: false },
  { label: "不確定", value: null },
];

const Chip = ({ text, active, onPress }: { text: string; active: boolean; onPress: () => void }) => (
  <button
    type="button"
    onClick={onPress}
    style={{
      padding: "10px 16px",
      borderRadius: 12,
      fontSize: 14,
      fontWeight: active ? 700 : 500,
      fontFamily: "inherit",
      color: active ? "#fff" : C.ink,
      background: active ? C.blue : "#fff",
      border: active ? `1.5px solid ${C.blue}` : "1.5px solid #E5E9F0",
      boxShadow: active ? "0 4px 12px -6px rgba(33,150,243,0.5)" : "none",
      cursor: "pointer",
      transition: "all 0.15s ease",
      whiteSpace: "nowrap",
    }}>
    {text}
  </button>
);

/** 複選用途：每列左側 checkbox */
const CheckOption = ({
  label,
  hint,
  active,
  onPress,
}: {
  label: string;
  hint?: string;
  active: boolean;
  onPress: () => void;
}) => (
  <button
    type="button"
    onClick={onPress}
    aria-pressed={active}
    style={{
      display: "flex",
      alignItems: "center",
      gap: 12,
      width: "100%",
      textAlign: "left",
      padding: "12px 14px",
      borderRadius: 14,
      fontFamily: "inherit",
      cursor: "pointer",
      background: active ? "rgba(33,150,243,0.08)" : "#fff",
      border: active ? `1.5px solid ${C.blue}` : "1.5px solid #E5E9F0",
      boxShadow: active ? "0 4px 12px -8px rgba(33,150,243,0.35)" : "none",
      transition: "all 0.15s ease",
    }}>
    <span
      aria-hidden
      style={{
        width: 22,
        height: 22,
        borderRadius: 6,
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: active ? C.blue : "#fff",
        border: `1.5px solid ${active ? C.blue : "#C5CCD8"}`,
        color: "#fff",
      }}>
      {active ? <RiCheckLine size={15} /> : null}
    </span>
    <span style={{ minWidth: 0, flex: 1 }}>
      <span style={{ display: "block", fontSize: 14.5, fontWeight: active ? 700 : 600, color: C.ink }}>
        {label}
      </span>
      {hint ? (
        <span style={{ display: "block", marginTop: 2, fontSize: 12, fontWeight: 500, color: C.muted }}>
          {hint}
        </span>
      ) : null}
    </span>
  </button>
);

/* ── 題目卡（白卡 + 序號 + 標題）── */
const QuestionCard = ({
  no,
  icon,
  title,
  hint,
  optional,
  children,
}: {
  no: number;
  icon: React.ReactNode;
  title: string;
  hint?: string;
  optional?: boolean;
  children: React.ReactNode;
}) => (
  <div
    style={{
      background: "#fff",
      borderRadius: 18,
      padding: "18px 16px",
      boxShadow: "0 3px 10px -8px rgba(31,41,55,0.18)",
    }}>
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
      <span
        style={{
          width: 34,
          height: 34,
          borderRadius: 10,
          background: "#EBF5FF",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}>
        {icon}
      </span>
      <span style={{ minWidth: 0 }}>
        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: C.blue }}>Q{no}</span>
          {optional && (
            <span style={{ fontSize: 10, fontWeight: 700, color: C.muted, background: "#F1F2F5", padding: "2px 7px", borderRadius: 6 }}>
              選填
            </span>
          )}
        </span>
        <span style={{ display: "block", fontSize: 15, fontWeight: 700, color: C.ink, marginTop: 1 }}>{title}</span>
      </span>
    </div>
    {hint && <div style={{ fontSize: 12, color: C.muted, margin: "2px 0 0 44px", lineHeight: 1.5 }}>{hint}</div>}
    <div style={{ marginTop: 14 }}>{children}</div>
  </div>
);

/* ── 完成畫面 ── */
const SurveyDone = ({ record, onBackHome }: { record: GasSurveyRecord; onBackHome: () => void }) => {
  const { predictedDays, dispatchLeadDays } = estimateCycleFromSurvey(record);

  return (
    <div style={{ padding: "10px 18px 24px", display: "flex", flexDirection: "column", gap: 16 }}>
      {/* 結果卡 */}
      <div
        style={{
          background: "#fff",
          borderRadius: 18,
          padding: "22px 18px",
          boxShadow: "0 3px 10px -8px rgba(31,41,55,0.18)",
          textAlign: "center",
        }}>
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: "50%",
            background: C.greenTint,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 12px",
          }}>
          <RiCheckLine size={28} color={C.green} />
        </div>
        <div style={{ fontSize: 18, fontWeight: 800, color: C.ink }}>已收到您的用氣習慣</div>
        <div style={{ fontSize: 13, color: C.muted, marginTop: 6, lineHeight: 1.6 }}>
          AI 會結合您的每次叫桶紀錄
          <br />
          持續修正預測，越用越準
        </div>

        <div
          style={{
            marginTop: 18,
            background: "#F8FAFD",
            borderRadius: 14,
            padding: "14px 12px",
            display: "flex",
            alignItems: "stretch",
          }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, color: C.muted, display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}>
              初步估算週期
              <span style={{ fontSize: 9, fontWeight: 700, padding: "1px 5px", borderRadius: 5, background: "rgba(252,211,77,0.3)", color: "#92400E" }}>
                估算
              </span>
            </div>
            <div style={{ fontSize: 26, fontWeight: 800, color: C.ink, marginTop: 4, fontVariantNumeric: "tabular-nums" }}>
              {predictedDays}
              <span style={{ fontSize: 13, fontWeight: 500, color: C.muted, marginLeft: 3 }}>天</span>
            </div>
          </div>
          <div style={{ width: 1, background: "#E5E9F0" }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, color: C.muted }}>建議提前預約</div>
            <div style={{ fontSize: 26, fontWeight: 800, color: C.ink, marginTop: 4, fontVariantNumeric: "tabular-nums" }}>
              {dispatchLeadDays}
              <span style={{ fontSize: 13, fontWeight: 500, color: C.muted, marginLeft: 3 }}>天</span>
            </div>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={onBackHome}
        style={{
          background: C.blue,
          borderRadius: 16,
          padding: "16px 18px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          boxShadow: "0 6px 16px -10px rgba(33,150,243,0.45)",
          cursor: "pointer",
          border: "none",
          width: "100%",
          textAlign: "left",
          fontFamily: "inherit",
        }}>
        <span>
          <span style={{ display: "block", color: "#fff", fontSize: 16, fontWeight: 700 }}>回到首頁</span>
          <span style={{ display: "block", color: "rgba(255,255,255,0.85)", fontSize: 11, marginTop: 2 }}>查看您的智慧預測</span>
        </span>
        <span
          style={{
            width: 36,
            height: 36,
            borderRadius: "50%",
            background: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}>
          <RiArrowRightLine size={18} color={C.blue} />
        </span>
      </button>
    </div>
  );
};

/**
 * 用氣習慣問卷（/survey）— 冷啟動 onboarding。
 * 訂單紀錄不足時，以問卷自陳 + 同類型先驗起步估算叫桶週期；
 * 目前為前端版（localStorage），後端 gas-survey API 就緒後改為送出。
 */
export const SurveyPage = () => {
  const navigate = useNavigate();
  const { chooseIdentity } = useUser();

  const [surveyDays, setSurveyDays] = useState<number | null>(null);
  const [customDays, setCustomDays] = useState("");
  const [cylinderKg, setCylinderKg] = useState<number | null | undefined>(undefined);
  const [lastOrderAgo, setLastOrderAgo] = useState<LastOrderAgoKey | null>(null);
  const [customAgoDays, setCustomAgoDays] = useState("");
  const [householdSize, setHouseholdSize] = useState<string | null>(null);
  const [usageTypes, setUsageTypes] = useState<string[]>([]);
  const [hasSpare, setHasSpare] = useState<boolean | null | undefined>(undefined);
  const [done, setDone] = useState<GasSurveyRecord | null>(null);

  const effectiveDays = useMemo(() => {
    const custom = Number(customDays);
    if (customDays && Number.isFinite(custom) && custom > 0) return Math.round(custom);
    return surveyDays;
  }, [customDays, surveyDays]);

  const toggleUsage = (value: string) =>
    setUsageTypes((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );

  const { cusJwtToken } = useCusAuth();
  const [submitting, setSubmitting] = useState(false);
  const canSubmit =
    !submitting &&
    effectiveDays !== null &&
    cylinderKg !== undefined &&
    householdSize !== null;

  const handleSubmit = async () => {
    if (!canSubmit || effectiveDays === null) return;
    const customAgo = Number(customAgoDays);
    const hasCustomAgo =
      customAgoDays !== "" && Number.isFinite(customAgo) && customAgo >= 0;
    const agoDays = hasCustomAgo
      ? Math.min(LAST_ORDER_AGO_DAYS_MAX, Math.round(customAgo))
      : (LAST_ORDER_AGO_CHIPS.find((c) => c.key === lastOrderAgo)?.days ?? null);
    const agoKey = hasCustomAgo
      ? mapDaysToLastOrderAgoKey(agoDays as number)
      : lastOrderAgo;
    const record: GasSurveyRecord = {
      survey_days: effectiveDays,
      cylinder_kg: cylinderKg ?? null,
      household_size: householdSize,
      usage_types: usageTypes.length > 0 ? usageTypes : null,
      last_order_ago: agoKey,
      last_order_ago_days: agoDays,
      has_spare_cylinder: hasSpare === undefined ? null : hasSpare,
      observed_interval_days: null,
      completed_at: new Date().toISOString(),
    };
    setSubmitting(true);
    try {
      const xContext = buildXContextHeader(chooseIdentity);
      if (!cusJwtToken || !xContext) {
        throw new Error("缺少登入或帳戶上下文，無法送出問卷");
      }
      await postGasSurvey({
        token: cusJwtToken,
        xContext,
        record,
      });
      // API 成功後才 cache／顯示完成
      saveGasSurvey(chooseIdentity?.context_id, record);
      setDone(record);
      window.scrollTo({ top: 0 });
    } catch (err) {
      console.warn("gas-survey submit failed; not marking complete", err);
      toast.error(toToastMessage((err as any)?.response?.data?.message, "問卷送出失敗，請確認已完成綁定後再試"));
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    boxSizing: "border-box",
    padding: "12px 14px",
    borderRadius: 12,
    border: "1.5px solid #E5E9F0",
    fontSize: 15,
    fontFamily: "inherit",
    color: C.ink,
    background: "#fff",
    outline: "none",
  };

  return (
    <div
      className="survey-scroll"
      style={{
        width: "100%",
        height: "100dvh",
        fontFamily: "var(--font-sans)",
        color: C.ink,
        overflowY: "auto",
        overflowX: "hidden",
        position: "relative",
        background: C.page,
        scrollbarWidth: "none",
        msOverflowStyle: "none" as any,
      }}>
      <style>{`.survey-scroll::-webkit-scrollbar { display:none; width:0; height:0; }`}</style>

      {/* ── 漸層 Hero ── */}
      <div
        style={{
          background: C.heroGradient,
          padding: "18px 20px 56px",
          color: "#fff",
          position: "relative",
        }}>
        <Grain o={0.03} />

        <button
          type="button"
          onClick={() => navigate("/home")}
          aria-label="返回首頁"
          style={{
            width: 36,
            height: 36,
            borderRadius: 12,
            border: "none",
            background: "rgba(255,255,255,0.18)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            position: "relative",
            zIndex: 2,
          }}>
          <RiArrowLeftSLine size={22} color="#fff" />
        </button>

        <div style={{ position: "relative", zIndex: 2, marginTop: 18 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <RiSparkling2Line size={17} color="rgba(255,255,255,0.85)" />
            <span style={{ fontSize: 14, color: "rgba(255,255,255,0.8)", fontWeight: 600 }}>智慧預測．用氣習慣</span>
          </div>
          {done ? (
            <>
              <div style={{ fontSize: 30, fontWeight: 800, letterSpacing: -0.5, marginTop: 10, lineHeight: 1.25 }}>完成！</div>
              <div style={{ fontSize: 14, color: "rgba(255,255,255,0.88)", marginTop: 8, lineHeight: 1.6 }}>
                AI 已開始為您估算叫桶週期
              </div>
            </>
          ) : (
            <>
              <div style={{ fontSize: 30, fontWeight: 800, letterSpacing: -0.5, marginTop: 10, lineHeight: 1.25 }}>
                幫 AI 認識你
              </div>
              <div style={{ fontSize: 14, color: "rgba(255,255,255,0.88)", marginTop: 8, lineHeight: 1.6 }}>
                您的叫桶紀錄還不多，回答幾題（約 2–3 分鐘）
                <br />
                即可搶先啟用智慧預測
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── 白色 sheet ── */}
      <div
        style={{
          background: C.page,
          borderRadius: "24px 24px 0 0",
          marginTop: -24,
          position: "relative",
          zIndex: 1,
          paddingTop: 10,
          paddingBottom: "calc(24px + env(safe-area-inset-bottom))",
        }}>
        {done ? (
          <SurveyDone record={done} onBackHome={() => navigate("/home")} />
        ) : (
          <div style={{ padding: "10px 18px 0", display: "flex", flexDirection: "column", gap: 14 }}>
            {/* Q1：預計多久叫一次 */}
            <QuestionCard
              no={1}
              icon={<RiTimerFlashLine size={17} color={C.blue} />}
              title="平常大概多久叫一次瓦斯？"
              hint="選個大概就好，後續會根據您的實際情況自動調整">
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {CYCLE_CHIPS.map((c) => (
                  <Chip
                    key={c.days}
                    text={c.label}
                    active={!customDays && surveyDays === c.days}
                    onPress={() => {
                      setSurveyDays(c.days);
                      setCustomDays("");
                    }}
                  />
                ))}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12 }}>
                <input
                  type="number"
                  inputMode="numeric"
                  min={1}
                  placeholder="或自行輸入天數，例如 45"
                  value={customDays}
                  onChange={(e) => setCustomDays(e.target.value)}
                  style={{ ...inputStyle, flex: 1 }}
                />
                <span style={{ fontSize: 14, color: C.muted, flexShrink: 0 }}>天</span>
              </div>
            </QuestionCard>

            {/* Q2：鋼瓶規格 */}
            <QuestionCard
              no={2}
              icon={<RiGasStationLine size={17} color={C.blue} />}
              title="家中使用的鋼瓶規格？"
              hint="桶子側面鋼印有標示公斤數；不確定也沒關係">
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {KG_CHIPS.map((k) => (
                  <Chip key={k.label} text={k.label} active={cylinderKg === k.kg} onPress={() => setCylinderKg(k.kg)} />
                ))}
              </div>
            </QuestionCard>

            {/* Q3（選填）：距上次叫桶 */}
            <QuestionCard
              no={3}
              icon={<RiHistoryLine size={17} color={C.blue} />}
              title="距上次叫桶大概多久了？"
              hint="幫助估算從哪一天開始算；不記得可跳過"
              optional>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {LAST_ORDER_AGO_CHIPS.map((c) => (
                  <Chip
                    key={c.key}
                    text={c.label}
                    active={!customAgoDays && lastOrderAgo === c.key}
                    onPress={() => {
                      setLastOrderAgo(c.key);
                      setCustomAgoDays("");
                    }}
                  />
                ))}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12 }}>
                <input
                  type="number"
                  inputMode="numeric"
                  min={0}
                  max={LAST_ORDER_AGO_DAYS_MAX}
                  placeholder="或自行輸入天數，例如 40"
                  value={customAgoDays}
                  onChange={(e) => {
                    setCustomAgoDays(e.target.value);
                    setLastOrderAgo(null);
                  }}
                  style={{ ...inputStyle, flex: 1 }}
                />
                <span style={{ fontSize: 14, color: C.muted, flexShrink: 0 }}>天前</span>
              </div>
            </QuestionCard>

            {/* Q4：家庭人數 */}
            <QuestionCard
              no={4}
              icon={<RiGroupLine size={17} color={C.blue} />}
              title="家中通常有幾個人？"
              hint="人數影響用氣量，幫助 AI 更準確估算週期">
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {HOUSEHOLD_CHIPS.map((h) => (
                  <Chip
                    key={h.value}
                    text={h.label}
                    active={householdSize === h.value}
                    onPress={() => setHouseholdSize(h.value)}
                  />
                ))}
              </div>
            </QuestionCard>

            {/* Q5（選填）：主要用途 多選 */}
            <QuestionCard
              no={5}
              icon={<RiFireLine size={17} color={C.blue} />}
              title="家中瓦斯主要用來做什麼？"
              hint="可複選，點一下勾選、再點取消"
              optional>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {USAGE_CHIPS.map((u) => (
                  <CheckOption
                    key={u.value}
                    label={u.label}
                    hint={u.hint}
                    active={usageTypes.includes(u.value)}
                    onPress={() => toggleUsage(u.value)}
                  />
                ))}
              </div>
              {usageTypes.length > 0 && (
                <div style={{ fontSize: 11.5, color: C.muted, marginTop: 10, lineHeight: 1.5 }}>
                  已選 {usageTypes.length} 項：
                  {usageTypes.map((v) => USAGE_CHIPS.find((u) => u.value === v)?.label).join("、")}
                </div>
              )}
            </QuestionCard>

            {/* Q6（選填）：備瓶 */}
            <QuestionCard
              no={6}
              icon={<RiInboxLine size={17} color={C.blue} />}
              title="家裡有備瓶嗎？"
              hint="有備瓶時，用完節奏可能不同"
              optional>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {SPARE_CHIPS.map((c) => (
                  <Chip
                    key={c.label}
                    text={c.label}
                    active={hasSpare === c.value}
                    onPress={() => setHasSpare(c.value)}
                  />
                ))}
              </div>
            </QuestionCard>

            {/* 送出 CTA */}
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit}
              style={{
                background: canSubmit ? C.blue : "#C4CBD6",
                borderRadius: 16,
                padding: "16px 18px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                boxShadow: canSubmit ? "0 6px 16px -10px rgba(33,150,243,0.45)" : "none",
                cursor: canSubmit ? "pointer" : "default",
                border: "none",
                width: "100%",
                textAlign: "left",
                marginTop: 4,
                fontFamily: "inherit",
                transition: "background 0.2s ease",
              }}>
              <span>
                <span style={{ display: "block", color: "#fff", fontSize: 16, fontWeight: 700 }}>完成，開始智慧預測</span>
                <span style={{ display: "block", color: "rgba(255,255,255,0.85)", fontSize: 11, marginTop: 2 }}>
                  {canSubmit ? "資料僅用於估算叫桶週期" : "請先完成 Q1、Q2、Q3"}
                </span>
              </span>
              <span
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  background: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}>
                {canSubmit ? <RiCheckLine size={18} color={C.blue} /> : <RiQuestionMark size={16} color="#C4CBD6" />}
              </span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SurveyPage;
